import _regeneratorRuntime from "/home/circleci/warehouse_client/node_modules/babel-preset-react-app/node_modules/@babel/runtime/regenerator";
import { useCallback } from 'react';
import { trackEvent } from 'web-services/GoogleAnalytics';
import { useMachine } from 'web-services/lib/hooks';
import { trackAuth } from 'web-services/segment';
import cookie from 'js-cookie';
import Mailcheck from 'mailcheck';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var SUGGESTED_EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'aol.com', 'hotmail.com', 'msn.com', 'outlook.com', 'modsy.com', 'mail.com', 'icloud.com'];
/**
 * This machine captures the business logic for creating users.
 * In this context, "creating" can mean either signing up or logging in.
 */

var states = {
  EDITING: 'editing',
  SUBMITTING: 'submitting',
  SUCCESS: 'success'
};
var events = {
  INPUT_BLUR: 'input-blur',
  INPUT_CHANGE: 'input-change',
  SUBMIT: 'submit'
};
var internalEvents = {
  SUBMIT_SUCCESS: 'submit-success',
  SUBMIT_ERROR: 'submit-error'
};
var initialContext = {
  skipPassword: false,
  email: {
    value: '',
    error: validate('email', ''),
    touched: false,
    suggestion: undefined
  },
  password: {
    value: '',
    error: validate('password', ''),
    touched: false
  },
  confirmPassword: {
    value: '',
    error: validate('confirmPassword', ''),
    touched: false
  },
  submitError: '',
  user: undefined
};

function makeCreateUserMachine(_ref) {
  var _on, _on2, _states;

  var id = _ref.id,
      createUserDataProxy = _ref.createUserDataProxy,
      logErrorProxy = _ref.logErrorProxy,
      onUserCreated = _ref.onUserCreated,
      skipPassword = _ref.skipPassword,
      mode = _ref.mode;
  return {
    id: id,
    context: _objectSpread2(_objectSpread2({}, initialContext), {}, {
      skipPassword: skipPassword,
      mode: mode
    }),
    initial: states.EDITING,
    states: (_states = {}, _defineProperty(_states, states.EDITING, {
      on: (_on = {}, _defineProperty(_on, events.INPUT_CHANGE, [{
        actions: [updateInputValue]
      }]), _defineProperty(_on, events.INPUT_BLUR, [{
        actions: [inputTouched]
      }]), _defineProperty(_on, events.SUBMIT, [{
        target: states.SUBMITTING,
        cond: allRequiredInputsAreValid
      }, {
        actions: [touchInputs]
      }]), _on)
    }), _defineProperty(_states, states.SUBMITTING, {
      invoke: {
        id: 'loginUser',
        src: createUser(createUserDataProxy)
      },
      on: (_on2 = {}, _defineProperty(_on2, internalEvents.SUBMIT_SUCCESS, [{
        target: states.SUCCESS,
        actions: [saveUser, setJwtCookie]
      }]), _defineProperty(_on2, internalEvents.SUBMIT_ERROR, [{
        target: states.EDITING,
        actions: [setSubmitError, logError(logErrorProxy)]
      }]), _on2)
    }), _defineProperty(_states, states.SUCCESS, {
      type: 'final'
    }), _states),
    onDone: onUserCreated
  };
}
/**
 * Define our transition guard conditions
 */


function allRequiredInputsAreValid(context, event) {
  var confirmPassword = context.confirmPassword,
      email = context.email,
      mode = context.mode,
      password = context.password,
      skipPassword = context.skipPassword;
  var noInputErrors = true;

  switch (mode) {
    case 'reset-password-request':
      noInputErrors = !email.error;
      break;

    case 'reset-password-submit':
      noInputErrors = !password.error && !confirmPassword.error;
      break;

    case 'set-password-submit':
      noInputErrors = !password.error && !confirmPassword.error;
      break;

    case 'login':
      noInputErrors = !email.error && !password.error;
      break;

    case 'signup':
      noInputErrors = !email.error && (skipPassword || !password.error);
      break;
  }

  return noInputErrors;
}
/**
 * Define our actions.
 */


function updateInputValue(context, event) {
  var _mergeContext;

  var _event$payload = event.payload,
      input = _event$payload.input,
      value = _event$payload.value;
  var mergeContext = (_mergeContext = {}, _defineProperty(_mergeContext, input, _objectSpread2(_objectSpread2({}, context[input]), {}, {
    value: value,
    error: input !== 'password' || context.skipPassword === false ? validate(input, value, context) : ''
  })), _defineProperty(_mergeContext, "submitError", ''), _mergeContext); // sets the suggested email provided by mailcheck

  if (input === 'email') {
    Mailcheck.run({
      email: value,
      domains: SUGGESTED_EMAIL_DOMAINS,
      secondLevelDomains: [],
      topLevelDomains: [],
      suggested: function suggested(suggestion) {
        mergeContext.email.suggestion = suggestion.full;
      },
      empty: function empty() {
        mergeContext.email.suggestion = undefined;
      }
    });
  } // update confirmPassword error when password changes


  if (input === 'password' && !context.skipPassword) {
    var confirmPassword = context.confirmPassword;
    var error = validate('confirmPassword', confirmPassword.value, mergeContext);
    mergeContext.confirmPassword = _objectSpread2(_objectSpread2({}, confirmPassword), {}, {
      error: error
    });
  }

  return mergeContext;
}

function inputTouched(context, event) {
  var input = event.payload;
  return _defineProperty({}, input, _objectSpread2(_objectSpread2({}, context[input]), {}, {
    touched: true
  }));
}

function touchInputs(context, event) {
  return {
    email: _objectSpread2(_objectSpread2({}, context.email), {}, {
      touched: true
    }),
    password: _objectSpread2(_objectSpread2({}, context.password), {}, {
      touched: true
    }),
    confirmPassword: _objectSpread2(_objectSpread2({}, context.confirmPassword), {}, {
      touched: true
    })
  };
}

function saveUser(context, event) {
  return {
    user: event.payload.user
  };
}

function setJwtCookie(context, event) {
  var mode = context.mode;

  if (mode === 'reset-password-request' || mode === 'reset-password-submit' || mode === 'set-password-submit') {
    return; // early exit, no token in these modes
  }

  var USER_TOKEN_NAME = 'jwtToken';
  var USER_TOKEN_EXPIRATION_DAYS = 30;
  var token = event.payload.user.token;
  cookie.set(USER_TOKEN_NAME, token, {
    expires: USER_TOKEN_EXPIRATION_DAYS
  });
}

function setSubmitError(context, event) {
  var error = event.payload.error;
  return {
    submitError: error.message
  };
}

function logError(logErrorProxy) {
  return function (context, event) {
    var error = event.payload.error;
    logErrorProxy(error);
  };
}
/**
 * Define our service callbacks.
 */


function createUser(createUserDataProxy) {
  return function (context) {
    return (
      /*#__PURE__*/
      function () {
        var _ref3 = _asyncToGenerator(
        /*#__PURE__*/
        _regeneratorRuntime.mark(function _callee(send) {
          var email, password, user;
          return _regeneratorRuntime.wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  _context.prev = 0;
                  email = context.email, password = context.password;
                  _context.next = 4;
                  return createUserDataProxy({
                    email: email.value,
                    password: password.value
                  });

                case 4:
                  user = _context.sent;
                  send({
                    type: internalEvents.SUBMIT_SUCCESS,
                    payload: {
                      user: user
                    }
                  });
                  _context.next = 11;
                  break;

                case 8:
                  _context.prev = 8;
                  _context.t0 = _context["catch"](0);
                  send({
                    type: internalEvents.SUBMIT_ERROR,
                    payload: {
                      error: _context.t0
                    }
                  });

                case 11:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee, null, [[0, 8]]);
        }));

        return function (_x) {
          return _ref3.apply(this, arguments);
        };
      }()
    );
  };
}
/**
 * Validation business logic.
 */


function validate() {
  var input = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var value = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';

  var _ref4 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      _ref4$email = _ref4.email,
      _ref4$password = _ref4.password,
      password = _ref4$password === void 0 ? '' : _ref4$password,
      _ref4$confirmPassword = _ref4.confirmPassword,
      _ref4$skipPassword = _ref4.skipPassword,
      skipPassword = _ref4$skipPassword === void 0 ? false : _ref4$skipPassword;

  switch (input) {
    case 'email':
      {
        var em = value.trim();

        if (!em) {
          return 'Email is required';
        } else if (!isValidEmail(em)) {
          return 'Invalid email address';
        }

        return '';
      }

    case 'password':
      {
        var pw = value.trim();

        if (!skipPassword && (!pw || pw.length < 6 || pw.length > 20)) {
          return 'Password must be 6 - 20 characters in length';
        }

        return '';
      }

    case 'confirmPassword':
      {
        var cpw = value.trim();

        if (cpw !== password.value) {
          return 'The passwords do not match';
        }

        return '';
      }

    default:
      return '';
  }
}
/**
 * Regular expression for determining whether or not an email is valid.
 * Not the most elegant solution but we have been using it for a long
 * time now and it does a good enough job.
 */


function isValidEmail(email) {
  // eslint-disable-next-line
  var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}
/**
 * Custom hook implementing the common business logic for user signup/login.
 * The business logic for signup, login, and password reset are similar enough
 * that we can reuse the majority of the logic across both and leverage a simple
 * mode param for the logic that differs.
 */


function useSignup(_ref) {
  var _ref$logErrorProxy = _ref.logErrorProxy,
      logErrorProxy = _ref$logErrorProxy === void 0 ? function () {} : _ref$logErrorProxy,
      _ref$mode = _ref.mode,
      mode = _ref$mode === void 0 ? 'signup' : _ref$mode,
      _ref$signupUserDataPr = _ref.signupUserDataProxy,
      signupUserDataProxy = _ref$signupUserDataPr === void 0 ? function (_ref2) {
    var email = _ref2.email,
        password = _ref2.password;
  } : _ref$signupUserDataPr,
      _ref$onSignedUp = _ref.onSignedUp,
      onSignedUp = _ref$onSignedUp === void 0 ? function (user) {} : _ref$onSignedUp,
      _ref$skipPassword = _ref.skipPassword,
      skipPassword = _ref$skipPassword === void 0 ? false : _ref$skipPassword; // This handler is called when the create user state machine
  // reaches its final state.

  var handleUserCreated = useCallback(function (_ref3) {
    var user = _ref3.user;

    if (mode === 'signup') {
      sendUserSignedUpAnalytics(user);
    } else if (mode === 'login') {
      sendUserLoggedInAnalytics(user);
    }

    if (typeof onSignedUp === 'function') {
      onSignedUp(user);
    }
  }, [onSignedUp]); // Spin up an instance of the create user state machine.

  var _useMachine = useMachine(makeCreateUserMachine({
    id: 'signup',
    createUserDataProxy: signupUserDataProxy,
    logErrorProxy: logErrorProxy,
    onUserCreated: handleUserCreated,
    skipPassword: skipPassword,
    mode: mode
  })),
      currentState = _useMachine.currentState,
      send = _useMachine.send;

  return {
    currentState: currentState,
    events: events,
    send: send,
    states: states
  };
}

function sendUserSignedUpAnalytics(_ref4) {
  var id = _ref4.id,
      created = _ref4.created,
      email = _ref4.email,
      firstName = _ref4.firstName,
      isAdmin = _ref4.isAdmin,
      lastName = _ref4.lastName,
      from = _ref4.from;
  trackAuth({
    userId: id,
    traits: {
      createdAt: created,
      email: email,
      firstName: firstName,
      isAdmin: isAdmin,
      lastName: lastName
    }
  });
  trackEvent('user_registered', {
    user_email: email,
    user_id: id,
    source: from
  });
  trackEvent('Customer Conversion', 'User Signup', from);
}

function sendUserLoggedInAnalytics(_ref5) {
  var id = _ref5.id,
      created = _ref5.created,
      email = _ref5.email,
      firstName = _ref5.firstName,
      isAdmin = _ref5.isAdmin,
      lastName = _ref5.lastName;
  trackAuth({
    userId: id,
    traits: {
      createdAt: created,
      email: email,
      firstName: firstName,
      isAdmin: isAdmin || false,
      lastName: lastName
    }
  });
}

export { useSignup };